import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SERVICES } from "../data/services";
import { Reveal, SectionHeading } from "../components/site/Reveal";

const SPANS = ["lg:col-span-3", "lg:col-span-3", "lg:col-span-2", "lg:col-span-2", "lg:col-span-2", "lg:col-span-3", "lg:col-span-3", "lg:col-span-2", "lg:col-span-2", "lg:col-span-2"];

const ServiceCard = ({ service, span, index }) => {
  const ref = useRef(null);
  const [hover, setHover] = useState(false);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 150, damping: 18 });
  const sry = useSpring(ry, { stiffness: 150, damping: 18 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    ry.set(px * 7);
    rx.set(-py * 7);
    ref.current.style.setProperty("--mx", `${e.clientX - r.left}px`);
    ref.current.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  const onLeave = () => {
    rx.set(0);
    ry.set(0);
    setHover(false);
  };

  return (
    <Reveal delay={(index % 3) * 0.08} className={span}>
      <Link to={`/${service.slug}`} aria-label={`Explorar ${service.title}`}>
        <motion.div
          ref={ref}
          onMouseMove={onMove}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={onLeave}
          style={{ rotateX: srx, rotateY: sry, transformPerspective: 900 }}
          whileHover={{ y: -6 }}
          className="group relative h-full overflow-hidden rounded-2xl border border-[var(--line)] bg-[var(--surface)]/70 p-7 md:p-8 transition-colors duration-300 hover:border-[var(--green)]/40"
          data-testid={`service-card-${service.slug}`}
          data-cursor="card"
        >
          <div
            className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
            style={{ background: "radial-gradient(320px circle at var(--mx, 50%) var(--my, 50%), rgba(0,199,165,0.12), transparent 65%)" }}
            aria-hidden="true"
          />
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[var(--cyan)]/50 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" aria-hidden="true" />
          <div className="relative flex h-full flex-col">
            <div className="flex items-start justify-between">
              <span className="mono text-xs text-[var(--green)]">{service.num}</span>
              <ArrowUpRight size={18} className="text-[var(--muted)] transition-all duration-300 group-hover:text-[var(--green)] group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </div>
            <h3 className="mt-8 text-xl md:text-2xl font-bold tracking-tight">{service.title}</h3>
            <p className={`mt-3 text-sm leading-relaxed text-[var(--muted)] transition-all duration-500 ${hover ? "opacity-100" : "opacity-70"}`}>
              {service.short}
            </p>
          </div>
        </motion.div>
      </Link>
    </Reveal>
  );
};

export const Services = () => (
  <section id="servicios" className="relative py-28 md:py-36 bg-[var(--bg2)]" data-testid="services-section">
    <div className="mx-auto max-w-7xl px-6">
      <SectionHeading
        eyebrow="Soluciones"
        title={<>Tecnología diseñada <span className="text-gradient">alrededor de tu negocio.</span></>}
        copy="Diez capacidades que trabajan juntas: de la estrategia a la ejecución, del software a la inteligencia artificial."
        testId="services-heading"
      />
      <Reveal className="mt-14">
        <div className="grid gap-5 md:grid-cols-3" data-testid="services-banner">
          {[
            { src: "photo-1763568258205-3cbcf3ac56c6", alt: "Pantalla de laptop mostrando líneas de código", label: "Software" },
            { src: "photo-1550751827-4bd374c3f58b", alt: "Panel LED teal de infraestructura tecnológica", label: "Automatización" },
            { src: "photo-1677442135703-1787eea5ce01", alt: "Circuito electrónico con cerebro de inteligencia artificial", label: "Soluciones IA" },
          ].map((img, i) => (
            <Reveal key={img.src} delay={i * 0.1}>
              <div className="group relative overflow-hidden rounded-2xl border border-[var(--line)]" data-cursor="card">
                <img
                  src={`https://images.unsplash.com/${img.src}?q=80&w=1200&auto=format&fit=crop`}
                  alt={img.alt}
                  loading="lazy"
                  className="h-52 md:h-60 w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg2)] via-transparent to-transparent" aria-hidden="true" />
                <div className="absolute inset-0 bg-gradient-to-r from-[var(--green)]/10 via-transparent to-[var(--blue)]/10 mix-blend-overlay" aria-hidden="true" />
                <span className="mono absolute bottom-4 left-5 rounded-lg glass px-3 py-1.5 text-[10px] tracking-[0.3em] uppercase text-[var(--cyan)]">
                  {img.label}
                </span>
              </div>
            </Reveal>
          ))}
        </div>
      </Reveal>
      <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-6">
        {SERVICES.map((s, i) => (
          <ServiceCard key={s.slug} service={s} span={SPANS[i]} index={i} />
        ))}
      </div>
    </div>
  </section>
);
